import { Children, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { NodeSupplierContract } from '@prisma/client';
import { UseMutationResult } from '@tanstack/react-query';
import { Button, ButtonProps, Form, FormItemProps, FormProps, Input, ResultProps, Space } from 'antd';
import { AxiosResponse } from 'axios';

import { CreateNodeDto, NodeWithDetails } from '@app/domain-t3-interfaces';

import { ApprovalStagePicker } from '../../approval-stages';

export type NodeFormData = {
  name: string;
  suppliers: NodeSupplierContract[];
  approvalFlow?: number[];
};

export const useNodeForm = (
  mutate: UseMutationResult<AxiosResponse<NodeWithDetails, unknown>, unknown, CreateNodeDto, unknown>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCancel?: (e: any) => void,
) => {
  const [form] = Form.useForm<NodeFormData>();
  const navigate = useNavigate();

  const getApprovalFlowFormItemProps = (): FormItemProps => ({
    children: <ApprovalStagePicker mode="tags" />,
    label: 'Approval Flow',
    name: 'approvalFlow',
  });

  const getNodeNameFormItemProps = (): FormItemProps => ({
    children: <Input />,
    label: 'Name',
    name: 'name',
    required: true,
    rules: [
      { required: true },
      { min: 1, message: 'Name must be at least 1 character.' },
      { max: 25, message: 'Name must be less than 25 characters.' },
      {
        pattern: new RegExp(/^([A-Z0-9_-]+)$/),
        message: 'Can only be capital letters, numbers, underscores, or dashes',
      },
    ],
  });

  const getFormProps = (): FormProps<NodeFormData> & { children: ReactNode } => ({
    children: (
      <>
        <Form.Item {...getNodeNameFormItemProps()} />
        <Form.Item {...getApprovalFlowFormItemProps()} />
        <Space>
          <Button {...getSaveButtonProps()} />
          <Button {...getResetButtonProps()} />
          <Button {...getCancelButtonProps()} />
        </Space>
      </>
    ),
    form,
    layout: 'vertical',
    onFinish: (formData) => {
      const createDto: CreateNodeDto = {
        name: formData.name.trim(),
        approvalFlow: formData.approvalFlow,
      };
      mutate.mutate(createDto);
    },
  });

  const getGotoNodesButtonProps = (): ButtonProps => ({
    children: 'Go to Nodes',
    type: 'primary',
    onClick: () => navigate('../'),
  });

  const getResetButtonProps = (): ButtonProps => ({
    children: 'Reset',
    onClick: () => form.resetFields(),
  });

  const getSaveButtonProps = (): ButtonProps => ({
    children: 'Save',
    htmlType: 'submit',
    loading: mutate.isPending,
    type: 'primary',
  });

  const getCancelButtonProps = (): ButtonProps => ({
    children: 'Cancel',
    onClick: onCancel ?? (() => navigate('../')),
  });

  const getCreateAnotherButtonProps = (): ButtonProps => ({
    children: 'Create Another',
    onClick: () => {
      mutate.reset();
      form.resetFields();
    },
  });

  const getSuccessResultProps = (props?: ResultProps): ResultProps => ({
    status: 'success',
    subTitle: `Node name: ${mutate.data?.data.name}`,
    ...props,
    extra: Children.toArray(props?.extra),
  });

  return {
    form,
    getCancelButtonProps,
    getCreateAnotherButtonProps,
    getFormProps,
    getGotoNodesButtonProps,
    getSuccessResultProps,
  };
};
